<script lang="ts">
  import GlobeAlt from "$assets/icons/globe-alt.svelte";
  import SectionTitle from "$lib/components/shared/SectionTitle.svelte";
  import { selectedOrgStore } from "$lib/stores/orgs";
  import { authStateStore } from "$lib/stores/user";
  import PageAccessDenied from "../PageAccessDenied.svelte";
  import Gears from "$assets/images/gears.png";
  import { onMount } from "svelte";
  import {
    emptyFindingsStore,
    processedBatchOrgSecurityFindingsStore,
    type SecurityFinding,
  } from "$lib/stores/orgSecurityFindings";
  import SeverityChartCard from "$lib/components/security/SeverityChartCard.svelte";
  import SecurityFindingsContainer from "$lib/components/security/SecurityFindingsContainer.svelte";
  import FindingStatsCard from "$lib/components/security/FindingStatsCard.svelte";
  import { mockFindingsData } from "$lib/components/security/mockFindingsData";
  import { PageLoadingSpinner } from "$lib/ui/loading-spinner";

  let { mock } = $props();
  let isLoadingPage = $state(true);
  let isEmptyFindings = $state<boolean | undefined>(undefined);

  let canView = $derived.by(() => {
    const userEmail = $authStateStore.userInfo?.email?.toLowerCase();
    return (
      !!userEmail &&
      !!$selectedOrgStore?.members &&
      !!$selectedOrgStore?.members.find(
        (o) => o.email?.toLowerCase() === userEmail && o.claims.includes("org:security_findings"),
      )
    );
  });

  let findings = $state<SecurityFinding[]>([]);
  let batchState = $derived.by(() => $processedBatchOrgSecurityFindingsStore);

  onMount(() => {
    if (!mock) {
      emptyFindingsStore.subscribe((empty) => {
        isEmptyFindings = empty;
        if (!empty) {
          processedBatchOrgSecurityFindingsStore.subscribe((batchData) => {
            findings = batchData.findings || [];
            if (batchData.findings && batchData.findings.length > 0) {
              isLoadingPage = false;
            }
            // Handle when initial loading is complete but no findings
            if (!batchData.isLoading && batchData.loadedCount === batchData.totalCount) {
              isLoadingPage = false;
            }
          });
        } else {
          findings = mockFindingsData;
          isLoadingPage = false;
        }
      });
    } else {
      findings = mockFindingsData;
      isLoadingPage = false;
      isEmptyFindings = true;
    }
  });
</script>

{#if isLoadingPage}
  <PageLoadingSpinner
    timeout={20_000}
    message={findings.length > 0 ? `Loading ${findings.length} security findings...` : "Loading security findings..."}
  />
{:else if canView && isEmptyFindings !== undefined}
  <div class="grow px-6 lg:px-10 py-5 sm:py-8 lg:py-10 text-offblack">
    <SectionTitle>Anomalous Permissions</SectionTitle>
    <div class="flex flex-col gap-2">
      {#if isEmptyFindings}
        <div class="bg-[#FFE2D4] rounded-sm px-6 text-offblack relative">
          <h2 class="font-semibold text-xl mb-2">Our agents are getting your security findings ready...</h2>
          <p class="text-offblack">This may take a while. Browse this sample report in the meantime.</p>
          <img src={Gears} alt="gears" class="absolute -bottom-10 h-[142px] sm:h-[180px] right-[2%] sm:right-[4%]" />
        </div>
      {/if}

      <!-- Simple Progress Bar -->
      {#if !isEmptyFindings && batchState && batchState.totalCount > 0 && (batchState.loadingProgress ?? 0) < 100}
        <div class="w-full bg-gray-200 rounded-full h-1">
          <div
            class="bg-blue-600 h-1 rounded-full transition-all duration-300"
            style="width: {batchState.loadingProgress || 0}%"
          ></div>
        </div>
      {/if}

      <div
        class="grid grid-cols-2 grid-rows-1 gap-2 auto-rows-fr aspect-[2/1] sm:aspect-[3/1] min-h-[200px] max-h-[300px]"
      >
        <FindingStatsCard {findings} />
        <SeverityChartCard {findings} />
      </div>

      <div class="rounded-lg bg-white shadow">
        <div class="px-4 py-4 sm:px-6 flex gap-2 items-center">
          <span class="text-blue-500"><GlobeAlt /></span>
          <h2 class="font-bold text-lg">Detected Issues</h2>
        </div>
        <div class="px-4 py-2 sm:px-6 flex flex-col gap-2">
          <SecurityFindingsContainer
            {findings}
            dataFullyLoaded={batchState?.loadingProgress === 100 || isEmptyFindings === true}
          />
        </div>
      </div>
    </div>
  </div>
{:else}
  <PageAccessDenied />
{/if}
